import { FC, useEffect, useState, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";

import YouTube from "react-youtube";
import MDButton from "components/MDButton";
import SandClock from "assets/Icons/sandClock.svg?react";

import { makeStyles } from "tss-react/mui";
import { styles, getToastStyles } from "./Step2.styles";
import Timer from "components/Timer/Timer";
import { useAppSelector } from "store/hooks"
import { getTestTypeValue } from "store/slices/test"
import useTimerResume from "hooks/useTimerResume.hook"
import useNestedStep from "hooks/useNestedStep.hook"
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { checkTranslationExists } from "utils/step.utils";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { getModelMetaDataTags } from "services/models";

/** Stable id for Step2 nested-step persistence (independent of wizard order) */
const PARENT_STEP = 'step2';
const TIMER_LENGTH = 300;

const notify = (msg: string, theme?: any) =>
  toast(msg, {
    position: "top-left",
    hideProgressBar: true,
    draggable: true,
    closeButton: false,
    style: { ...getToastStyles(theme), whiteSpace: "pre-line" },
  });

interface StepProps {
  handleUpdateStep: (step: number) => void;
}

type Step1Map = {
  [key: number]: {
    Component: ReactElement;
    nextButtonText: string;
  };
};

const InnerStep1: FC<any> = ({ classes, youTubeOptions, onReady }) => {
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);

  // Dynamically find all text items
  const textItems = useMemo(() => {
    const items: string[] = [];
    const baseKey = `${testTypeLocaleKey}:Step2Screen:text`;
    let i = 1;
    while (checkTranslationExists(baseKey, i, t)) {
        items.push(t(`${baseKey}${i}`));
      i++;
    }
    return items;
  }, [t, testTypeLocaleKey]);

  const onVideoPlayedHandler = (event: any) => {
    event.target.playVideo();

    const videoId = t(`${testTypeLocaleKey}:Step2Screen:youtubeVideo`);
    sentryLogMessage({
      message: "Step2 tutorial video played",
      level: "info",
      tags: {
        component: "Step2",
        category: "video_played",
        event_type: SENTRY_EVENT_TYPES.VIEWED_TUTORIAL_VIDEO,
        tutorial_screen: "Step2Screen",
        ...getModelMetaDataTags(),
      },
      extra: {
        videoId,
        timestamp: new Date().toISOString(),
      },
    });
  };

  return (
    <>
      <span className={classes.title}>{t(`${testTypeLocaleKey}:Step2Screen:subtitle`)}</span>
      {textItems.map((text, index) => (
        <div key={index} className={classes.text}>
          <div className={classes.textBullet}>{index + 1}.&nbsp;&nbsp;</div>
          <span>{text}</span>
        </div>
      ))}
      <div className={classes.youTubeContainer}>
        {youTubeOptions ? (
          <YouTube
            iframeClassName={classes.iframeClassName}
            videoId={t(`${testTypeLocaleKey}:Step2Screen:youtubeVideo`)}
            opts={youTubeOptions}
            onReady={onReady}
            onPlay={onVideoPlayedHandler}
          />
        ) : null}
      </div>
    </>
  );
};
const InnerStep2: FC<any> = ({ classes, setTimerDone }) => {
  const { t } = useTranslation()
  const testType = useAppSelector(getTestTypeValue)
  const testTypeLocaleKey = getTestTypeLocaleKey(testType)
  const { sessionId, timerId } = useTimerResume('step2Timer')

  return (
    <div className={classes.innerStep2Container}>
      <div className={classes.sandClockContainer}>
        <SandClock />
      <div className={classes.timerText1}>{t(`${testTypeLocaleKey}:Step2Screen:timerText1`)}</div>
      </div>
      <Timer
        timerLength={TIMER_LENGTH}
        sessionId={sessionId}
        timerId={timerId}
        setTimerDone={setTimerDone}
      />
    </div>
  )
}

const Step2: FC<StepProps> = ({ handleUpdateStep }) => {
  const [step1InnerStep, setStep1InnerStep] = useNestedStep(PARENT_STEP)

  const [youTubeOptions, setYouTubeOptions] = useState<Record<string, number> | undefined>()
  const [timerDone, setTimerDone] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const testType = useAppSelector(getTestTypeValue)
  const testTypeLocaleKey = getTestTypeLocaleKey(testType)
  const isModelsLoaded = useAppSelector(state => state.imageRecognition.isModelsLoaded)

  const useStyles = makeStyles()(() => styles(theme))
  const { classes } = useStyles()

  const onReady = (event: any) => {
    event.target.pauseVideo()
  }

  useEffect(() => {
    const applyYouTubeSize = (container: HTMLElement) => {
      const youTubePlayerWidth = container.clientWidth
      setYouTubeOptions({
        height: 216,
        width: youTubePlayerWidth,
      })
    }

    const container = document.getElementById("Step2Container")
    if (!container) {
      const timeoutId = setTimeout(() => {
        const retryContainer = document.getElementById("Step2Container")
        if (retryContainer) {
          applyYouTubeSize(retryContainer)
        } else {
          console.error("[Step2] Step2Container not found after retry — YouTube player will not mount")
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }

    applyYouTubeSize(container)
  }, [])

  useEffect(() => {
    if (!isModelsLoaded) {
      notify(t(`${testTypeLocaleKey}:Step2Screen:badConnection`), theme)
    }
  }, [])

  const body: Step1Map = useMemo(() => {
    return {
      1: {
        Component: (
          <InnerStep1
            classes={classes}
            youTubeOptions={youTubeOptions}
            onReady={onReady}
          />
        ),
        nextButtonText: t(`${testTypeLocaleKey}:Step2Screen:nextButton1`),
      },
      2: {
        Component: <InnerStep2 classes={classes} setTimerDone={setTimerDone} />,
        nextButtonText: t(`${testTypeLocaleKey}:Step2Screen:nextButton2`),
      },
    }
  }, [classes, youTubeOptions, onReady, testTypeLocaleKey, t, setTimerDone])

  const handleNext = () => {
    if (step1InnerStep === 1) {
      // start timer
      setStep1InnerStep(2)
    }
    if (step1InnerStep === 2) {
      handleUpdateStep(4)
    }
  }

  return (
    <div id="Step2Container" className={classes.container}>
      <div className={classes.bodyContainer}>
        {body[step1InnerStep].Component}
      </div>

      <MDButton
        style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
        color={"primary"}
        variant="contained"
        type="submit"
        onClick={handleNext}
        disabled={step1InnerStep === 2 && !timerDone}
      >
        {body[step1InnerStep].nextButtonText}
      </MDButton>
    </div>
  )
}

export default Step2
